import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, TrendingUp, FileText, FlaskConical, Package, Zap, AlertCircle, Filter, X } from 'lucide-react';
import {
  Cell, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Sector
} from 'recharts';
import { toast } from 'sonner';
import ProblemDetailModal from '../components/ProblemDetailModal';
import { fetchProblems, updateProblem, deleteProblem, fetchDocuments } from '../utils/supabaseHelpers';
import { supabase } from '../supabaseClient';
import { formatSimpleDate } from '../utils/dateFormatter';
import '../animations.css';

// --- CUSTOM TOOLTIP COMPONENT ---
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-card p-3 border border-white/10 shadow-xl backdrop-blur-xl bg-[#1a1f2e]/90">
        <p className="text-gray-300 font-medium text-sm mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-xs mb-1 last:mb-0">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
            <span className="text-gray-400 capitalize">{entry.name}:</span>
            <span className="font-bold text-white">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// --- ACTIVE SHAPE FOR PIE CHART ---
const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 4}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 8}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <circle cx={cx} cy={cy} r={innerRadius - 5} stroke={fill} strokeWidth={1} fill="none" opacity={0.2} />
    </g>
  );
};

const RADIAN = Math.PI / 180;

const Overview = () => {
  const navigate = useNavigate();
  const [problems, setProblems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // Interactivity State
  const [activeIndex, setActiveIndex] = useState(0);
  const [filterType, setFilterType] = useState('task'); // 'task', 'experiment', 'document'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch Tasks
      const problemsData = await fetchProblems();
      setProblems(problemsData || []);

      // Fetch Documents
      const docsData = await fetchDocuments();
      setDocuments(docsData || []);

      // Fetch Experiments & Projects
      const { data: experimentsData, error } = await supabase
        .from('experiments')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (experimentsData) {
        setProjects(experimentsData.filter(item => item.type === 'project'));
        setExperiments(experimentsData.filter(item => item.type === 'experiment'));
      }

    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  // --- KPI CALCULATIONS ---
  const activeProjects = projects.filter(p => p.status === 'In Progress' || !p.status).length;

  const totalTasks = problems.length;

  const completedTasks = problems.filter(p => p.status === 'Done').length;

  const inProgressTasks = problems.filter(p => p.status === 'In Progress').length;
  
  const notDecidedTasks = problems.filter(p => p.status === 'Not Decided').length;
  
  const successfulExperiments = experiments.filter(e => e.status === 'Success').length;

  // --- CHART DATA PREPARATION ---

  // 1. Task Priority Distribution (Pie)
  // Helper to normalize priority
  const countPriority = (prio) => problems.filter(p => (p.priority || '').toLowerCase() === prio.toLowerCase()).length;

  const priorityDistribution = [
    { name: 'Critical', label: 'Critical', value: countPriority('Critical'), fill: '#ef4444' },
    { name: 'High', label: 'High', value: countPriority('High'), fill: '#f97316' },
    { name: 'Medium', label: 'Medium', value: countPriority('Medium'), fill: '#3b82f6' },
    { name: 'Low', label: 'Low', value: countPriority('Low'), fill: '#10b981' },
  ].filter(item => item.value > 0);

  // 2. Project Health (Radar)
  // Normalize metrics to 0-100 scale for Radar Chart
  const radarData = projects.slice(0, 6).map(project => {
    const projExps = experiments.filter(e => e.project_id === project.id);
    const projTasks = problems.filter(p => p.project_id === project.id); // Assuming tasks have project_id, or we map by name

    const volume = Math.min(projExps.length * 10, 100); // Cap at 10 experiments = 100
    const success = projExps.length > 0
      ? (projExps.filter(e => e.status === 'Success').length / projExps.length) * 100
      : 0;
    const velocity = Math.min(projExps.filter(e => {
      const d = new Date(e.created_at);
      const now = new Date();
      return (now - d) / (1000 * 60 * 60 * 24) <= 30; // Last 30 days
    }).length * 20, 100); // 5 recent experiments = 100

    return {
      subject: project.title.length > 10 ? project.title.substring(0, 10) + '..' : project.title,
      fullTitle: project.title,
      Volume: Math.round(volume),
      Success: Math.round(success),
      Velocity: Math.round(velocity),
      fullData: project
    };
  });

  // 3. R&D Velocity (Gradient Area)
  const getLast30Days = () => {
    const days = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  };

  const trendData = getLast30Days().map(date => {
    const experimentsLogged = experiments.filter(e => e.created_at?.split('T')[0] === date).length;
    const tasksSolved = problems.filter(p => p.status === 'Done' && (p.updated_at?.split('T')[0] === date || p.created_at?.split('T')[0] === date)).length;
    return {
      date: date.slice(5), // MM-DD
      Experiments: experimentsLogged,
      TasksSolved: tasksSolved
    };
  });

  // 4. Combined Activity Stream (Filtered strictly by Category)
  const combinedActivity = (() => {
    let rawList = [];
    if (filterType === 'task') {
      rawList = problems.map(p => ({ ...p, type: 'task', date: p.updated_at || p.created_at }));
    } else if (filterType === 'experiment') {
      rawList = experiments.map(e => ({ ...e, type: 'experiment', date: e.created_at }));
    } else if (filterType === 'document') {
      rawList = documents.map(d => ({ ...d, type: 'document', date: d.created_at }));
    }

    return rawList
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 6);
  })();

  // Helper for styles
  const getStatusStyle = (status) => {
    switch (status) {
      case 'Success': return 'border border-green-400 text-green-400 bg-green-400/10';
      case 'Failure': return 'border border-red-400 text-red-400 bg-red-400/10';
      case 'In Progress': return 'border border-blue-400 text-blue-400 bg-blue-400/10';
      case 'Done': return 'border border-green-400 text-green-400 bg-green-400/10';
      case 'Critical': return 'border border-red-500 text-red-500 bg-red-500/10';
      default: return 'border border-gray-400 text-gray-400 bg-gray-400/10';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading command center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="overview-page">


      {/* KPI Row (The Pulse) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-8">
        <div 
          className="glass-card p-3 lg:p-6 relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300 cursor-pointer"
          onClick={() => navigate('/experiment-tracking')}
        >
          <div className="absolute top-0 right-0 p-2 lg:p-4 opacity-10 group-hover:opacity-20 transition-opacity">
            <Package size={48} className="lg:w-16 lg:h-16" />
          </div>
          <div className="flex items-center justify-between mb-2 lg:mb-4 relative z-10">
            <div className="text-gray-400 text-xs lg:text-sm font-medium">Active Projects</div>
            <Package className="text-indigo-400" size={20} />
          </div>
          <div className="text-2xl lg:text-3xl font-bold text-white relative z-10">{activeProjects}</div>
          <div className="text-[10px] lg:text-xs text-indigo-400 mt-1 lg:mt-2 relative z-10">Running Research Initiatives</div>
        </div>

        <div 
          className="glass-card p-3 lg:p-6 relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300 cursor-pointer"
          onClick={() => navigate('/tasks')}
        >
          <div className="absolute top-0 right-0 p-2 lg:p-4 opacity-10 group-hover:opacity-20 transition-opacity">
            <CheckCircle size={48} className="lg:w-16 lg:h-16" />
          </div>
          <div className="flex items-center justify-between mb-2 lg:mb-4 relative z-10">
            <div className="text-gray-400 text-xs lg:text-sm font-medium">Total Tasks</div>
            <CheckCircle className="text-teal-400" size={20} />
          </div>
          <div className="text-2xl lg:text-3xl font-bold text-teal-400 relative z-10">{totalTasks}</div>
          <div className="text-[10px] lg:text-xs text-gray-400 mt-1 lg:mt-2 relative z-10">Total active tasks</div>
        </div>

        <div 
          className="glass-card p-3 lg:p-6 relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300 cursor-pointer"
          onClick={() => navigate('/tasks', { state: { filter: { status: 'In Progress' } } })}
        >
          <div className="absolute top-0 right-0 p-2 lg:p-4 opacity-10 group-hover:opacity-20 transition-opacity">
            <Clock size={48} className="lg:w-16 lg:h-16" />
          </div>
          <div className="flex items-center justify-between mb-2 lg:mb-4 relative z-10">
            <div className="text-gray-400 text-xs lg:text-sm font-medium">In Progress</div>
            <Clock className="text-blue-400" size={20} />
          </div>
          <div className="text-2xl lg:text-3xl font-bold text-blue-400 relative z-10">{inProgressTasks}</div>
          <div className="text-[10px] lg:text-xs text-gray-400 mt-1 lg:mt-2 relative z-10">Tasks currently being worked on</div>
        </div>

        <div 
          className="glass-card p-3 lg:p-6 relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300 cursor-pointer"
          onClick={() => navigate('/tasks', { state: { filter: { status: 'Not Decided' } } })}
        >
          <div className="absolute top-0 right-0 p-2 lg:p-4 opacity-10 group-hover:opacity-20 transition-opacity">
            <AlertCircle size={48} className="lg:w-16 lg:h-16" />
          </div>
          <div className="flex items-center justify-between mb-2 lg:mb-4 relative z-10">
            <div className="text-gray-400 text-xs lg:text-sm font-medium">Not Decided</div>
            <AlertCircle className="text-gray-400" size={20} />
          </div>
          <div className="text-2xl lg:text-3xl font-bold text-gray-400 relative z-10">{notDecidedTasks}</div>
          <div className="text-[10px] lg:text-xs text-gray-400 mt-1 lg:mt-2 relative z-10">Tasks pending decision</div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

        {/* Left Column: Charts */}
        <div className="lg:col-span-2 space-y-6">

          {/* Row 1: Experiment Outcomes & Project Health Radar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Task Priority Distribution (Interactive Pie) */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-6 text-white flex items-center gap-2">
                <TrendingUp size={20} className="text-blue-400" />
                Task Distribution
              </h3>
              <div className="flex items-center justify-center">
                {/* Chart Section */}
                <div className="h-[250px] w-full relative flex items-center justify-center">
                  {priorityDistribution.length > 0 ? (
                    <>
                      {/* Center Text Overlay */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
                        <span className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
                          {priorityDistribution[activeIndex] !== null ? priorityDistribution[activeIndex]?.label : 'Total Tasks'}
                        </span>
                        <span className="text-3xl font-bold text-white transition-all duration-300">
                          {priorityDistribution[activeIndex] !== null ? priorityDistribution[activeIndex]?.value : totalTasks}
                        </span>
                      </div>

                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                            data={priorityDistribution}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={110}
                            fill="#8884d8"
                            dataKey="value"
                            onMouseEnter={onPieEnter}
                            onMouseLeave={() => setActiveIndex(null)}
                            onClick={(data, index) => {
                              navigate('/tasks', { 
                                state: { 
                                  filter: { priority: data.name } 
                                } 
                              });
                            }}
                            className="cursor-pointer focus:outline-none"
                            stroke="none"
                          >
                            {priorityDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} stroke="rgba(0,0,0,0)" />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-2">
                      <div className="w-16 h-16 rounded-full border-4 border-gray-700/50 flex items-center justify-center">
                        <span className="text-xl font-bold text-gray-600">0</span>
                      </div>
                      <span className="text-sm">No tasks found</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Project Health (Radar) */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4 text-white">Project Health Radar</h3>
              <div className="h-[250px]">
                {radarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart 
                      cx="50%" 
                      cy="50%" 
                      outerRadius="80%" 
                      data={radarData}
                      onClick={(data) => {
                        if (data && data.activePayload && data.activePayload[0]) {
                          const projectData = data.activePayload[0].payload;
                          if (projectData && projectData.fullData) {
                            navigate(`/project-detail/${projectData.fullData.id}`);
                          }
                        }
                      }}
                    >
                      <PolarGrid stroke="rgba(255,255,255,0.1)" />
                      <PolarAngleAxis 
                        dataKey="subject" 
                        tick={(props) => {
                          const { x, y, payload, index } = props;
                          const projectData = radarData[index];
                          return (
                            <g>
                              <text
                                x={x}
                                y={y}
                                textAnchor={x > 150 ? 'start' : x < 150 ? 'end' : 'middle'}
                                fill="#9ca3af"
                                fontSize={10}
                                className="cursor-pointer hover:fill-blue-400 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (projectData && projectData.fullData) {
                                    navigate(`/project-detail/${projectData.fullData.id}`);
                                  }
                                }}
                              >
                                {payload.value}
                              </text>
                            </g>
                          );
                        }}
                      />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar
                        name="Project Metrics"
                        dataKey="Success"
                        stroke="#8884d8"
                        strokeWidth={2}
                        fill="#8884d8"
                        fillOpacity={0.3}
                        className="cursor-pointer"
                      />
                      <Radar
                        name="Velocity"
                        dataKey="Velocity"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        fill="#82ca9d"
                        fillOpacity={0.3}
                        className="cursor-pointer"
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">No project data yet</div>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: R&D Velocity (Gradient Area) */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 text-white">R&D Velocity (30 Days)</h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorExp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorTask" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="date" stroke="#6b7280" fontSize={12} tickMargin={10} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="Experiments"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorExp)"
                  />
                  <Area
                    type="monotone"
                    dataKey="TasksSolved"
                    stroke="#10b981"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorTask)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Right Column: Activity Stream (Updated) */}
        <div className="glass-card p-6 flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock size={20} className="text-blue-400" />
              Recent Activity
            </h3>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-1 text-sm text-gray-300 focus:outline-none focus:bg-white/10 focus:border-blue-500 transition-colors"
            >
              <option value="task" className="bg-[#1a1f2e]">Tasks</option>
              <option value="experiment" className="bg-[#1a1f2e]">Experiments</option>
              <option value="document" className="bg-[#1a1f2e]">Documents</option>
            </select>
          </div>

          <div className="flex-1 overflow-y-auto pr-2 space-y-4 scrollbar-thin">
            {combinedActivity.map((item, index) => (
              <div key={`${item.type}-${item.id}`} className="relative pl-6 pb-4 border-l border-white/10 last:border-0 last:pb-0 animate-in fade-in slide-in-from-left-4 duration-500" style={{ animationDelay: `${index * 100}ms` }}>
                <div className={`absolute left-[-5px] top-0 w-2.5 h-2.5 rounded-full ring-4 ring-[#1a1f2e] ${item.type === 'experiment' ? 'bg-teal-400' :
                  item.type === 'document' ? 'bg-purple-400' : 'bg-blue-500'
                  }`} />

                <div className="text-xs text-gray-500 mb-1">{formatSimpleDate(item.date)}</div>

                {item.type === 'experiment' && (
                  <div>
                    <div className="text-sm font-medium text-teal-400">Experiment Logged</div>
                    <div className="text-sm text-white mt-0.5 font-medium">{item.title}</div>
                    <div className="text-xs text-gray-400 mt-1">Status: {item.status}</div>
                  </div>
                )}

                {item.type === 'document' && (
                  <div>
                    <div className="text-sm font-medium text-purple-400">Document Uploaded</div>
                    <div className="text-sm text-white mt-0.5 font-medium flex items-center gap-2">
                      <FileText size={14} className="text-gray-400" />
                      {item.filename}
                    </div>
                    {item.category && <div className="text-xs text-gray-400 mt-1">{item.category}</div>}
                  </div>
                )}

                {item.type === 'task' && (
                  <div
                    className="cursor-pointer hover:bg-white/5 p-2 -ml-2 rounded-lg transition-colors"
                    onClick={() => {
                      setSelectedProblem(item);
                      setShowDetailModal(true);
                    }}
                  >
                    <div className="text-sm font-medium text-blue-400">
                      {item.status === 'Done' ? 'Task Completed' : 'Task Updated'}
                    </div>
                    <div className="text-sm text-white mt-0.5 font-medium">{item.title}</div>
                    <div className="flex gap-2 mt-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${getStatusStyle(item.priority)}`}>
                        {item.priority}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {combinedActivity.length === 0 && (
              <div className="text-center text-gray-500 py-8 flex flex-col items-center">
                <Filter size={32} className="mb-2 opacity-50" />
                <p>No recent {filterType}s found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal for Tasks */}
      {showDetailModal && selectedProblem && (
        <ProblemDetailModal
          problem={selectedProblem}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedProblem(null);
          }}
          onEdit={() => { }}
          onDelete={async () => {
            try {
              await deleteProblem(selectedProblem.id);
              setProblems(problems.filter(p => p.id !== selectedProblem.id));
              toast.success('Problem deleted successfully');
              setShowDetailModal(false);
              setSelectedProblem(null);
              loadData(); // Reload to update charts
            } catch (error) {
              toast.error('Failed to delete: ' + error.message);
            }
          }}
          onUpdate={async (updatedProblem) => {
            try {
              const updateData = {
                status: updatedProblem.status
              };

              if (updatedProblem.status === 'Done' && updatedProblem.done_date) {
                updateData.done_date = updatedProblem.done_date;
              } else if (updatedProblem.status !== 'Done') {
                updateData.done_date = null;
              }

              await updateProblem(updatedProblem.id, updateData);
              toast.success('Status updated');
              loadData(); // Reload to update charts
            } catch (error) {
              toast.error('Failed to update: ' + error.message);
            }
          }}
        />
      )}
    </div>
  );
};

export default Overview;
